import { computed, inject } from "@angular/core";
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { forkJoin, pipe, switchMap, tap } from "rxjs";
import { AuthService } from "./auth.service";
import { UserContextClient } from "./user-context.client";
import {
  findSalesOrgForCountry,
  getAllAvailableCountries,
  getFirstAllowedLanguage,
  MOCK_DEFAULT_COUNTRY,
  MOCK_DEFAULT_LANGUAGE,
} from "./user-context.mock";
import { SalesOrganization, UserContext } from "./user-context.model";
import {
  readStoredCountry,
  readStoredLanguage,
  storeCountry,
  storeLanguage,
} from "./user-context.storage";

interface UserContextState {
  userContext: UserContext | null;
  salesOrgs: SalesOrganization[];
  country: string;
  language: string;
  status: "idle" | "loading" | "ready" | "error";
  error: unknown;
}

const initialState: UserContextState = {
  userContext: null,
  salesOrgs: [],
  country: readStoredCountry() ?? MOCK_DEFAULT_COUNTRY,
  language: readStoredLanguage() ?? MOCK_DEFAULT_LANGUAGE,
  status: "idle",
  error: null,
};

export const userContextStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed((store) => ({
    availableCountries: computed(() =>
      getAllAvailableCountries(store.salesOrgs()),
    ),
    currentSalesOrg: computed(() =>
      findSalesOrgForCountry(store.salesOrgs(), store.country()),
    ),
    availableLanguages: computed(
      () =>
        findSalesOrgForCountry(store.salesOrgs(), store.country())
          ?.allowedLanguages ?? [],
    ),
    userName: computed(() => store.userContext()?.name ?? ""),
    userEmail: computed(() => store.userContext()?.email ?? ""),
    userGivenName: computed(() => store.userContext()?.givenName ?? ""),
    userFamilyName: computed(() => store.userContext()?.familyName ?? ""),
  })),
  withMethods(
    (
      store,
      client = inject(UserContextClient),
      auth = inject(AuthService),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { status: "loading", error: null })),
          switchMap(() =>
            forkJoin({
              ctx: client.loadUserContext(),
              profile: auth.getProfile(),
            }).pipe(
              tap({
                next: ({ ctx, profile }) => {
                  const salesOrgs = ctx.salesOrganizations;
                  const storedCountry = readStoredCountry();
                  const storedLanguage = readStoredLanguage();
                  const availableCountries =
                    getAllAvailableCountries(salesOrgs);

                  const country =
                    storedCountry && availableCountries.includes(storedCountry)
                      ? storedCountry
                      : MOCK_DEFAULT_COUNTRY;

                  const allowedLangs =
                    findSalesOrgForCountry(salesOrgs, country)
                      ?.allowedLanguages ?? [];
                  const language =
                    storedLanguage && allowedLangs.includes(storedLanguage)
                      ? storedLanguage
                      : (allowedLangs[0] ?? MOCK_DEFAULT_LANGUAGE);

                  const userContext: UserContext = {
                    ...ctx,
                    name:
                      profile?.name ??
                      profile?.preferred_username ??
                      profile?.sub ??
                      ctx.name,
                    email: profile?.email ?? ctx.email,
                    givenName: profile?.given_name,
                    familyName: profile?.family_name,
                  };

                  patchState(store, {
                    userContext,
                    salesOrgs,
                    country,
                    language,
                    status: "ready",
                    error: null,
                  });
                  storeCountry(country);
                  storeLanguage(language);
                },
                error: (error) => patchState(store, { status: "error", error }),
              }),
            ),
          ),
        ),
      ),

      setCountry(country: string): void {
        const salesOrgs = store.salesOrgs();
        const allowedLangs =
          findSalesOrgForCountry(salesOrgs, country)?.allowedLanguages ?? [];
        const currentLang = store.language();
        const language = allowedLangs.includes(currentLang)
          ? currentLang
          : (allowedLangs[0] ?? getFirstAllowedLanguage(salesOrgs, country));

        patchState(store, { country, language });
        storeCountry(country);
        storeLanguage(language);
      },

      setLanguage(language: string): void {
        patchState(store, { language });
        storeLanguage(language);
      },
    }),
  ),
  withHooks({
    onInit(store) {
      store.load();
    },
  }),
);
